import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Gamepad2, 
  Calendar, Download, BarChart3, PieChart, Activity, Zap, Package, Receipt, FileText
} from 'lucide-react';
import OperationalExpensesModal from './OperationalExpensesModal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AnalyticsDashboard() {
  const [period, setPeriod] = useState('today'); // today, this_week, this_month, custom
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [loading, setLoading] = useState(false); // Start with false, only show loading on filter changes
  const [showExpensesModal, setShowExpensesModal] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true); // Track initial load
  
  // Data states
  const [branches, setBranches] = useState([]);
  const [analytics, setAnalytics] = useState({
    revenue: {
      total: 0,
      ps: 0,
      fnb: 0,
      growth: 0
    },
    transactions: {
      total: 0,
      ps: 0,
      fnb: 0,
      avgValue: 0
    },
    costs: {
      fnbCogs: 0,
      electricity: 0,
      operational: 0,
      operationalBreakdown: [],
      total: 0
    },
    profit: {
      gross: 0,
      margin: 0
    },
    topProducts: [],
    allProducts: [],
    psUtilization: [],
    peakHours: []
  });

  const token = localStorage.getItem('token');

  // Fetch branches only once on mount
  useEffect(() => {
    fetchBranches();
    // Initial load - fetch immediately without debounce
    setLoading(true);
    fetchAnalytics().then(() => {
      setInitialLoad(false);
    });
  }, []);

  // Debounce analytics fetch to avoid excessive API calls on filter changes
  useEffect(() => {
    // Skip on initial load (already fetched above)
    if (initialLoad) {
      return;
    }

    // Skip fetch if custom range is incomplete
    if (period === 'custom' && (!customRange.start || !customRange.end)) {
      return;
    }

    // Set loading before debounce
    setLoading(true);

    // Debounce: Wait 800ms after user stops changing filters
    const debounceTimer = setTimeout(() => {
      fetchAnalytics();
    }, 800);

    return () => clearTimeout(debounceTimer);
  }, [period, selectedBranch, customRange]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.branches || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      // OPTIMIZED: Use new comprehensive analytics endpoint
      let startDate, endDate, dateFilter;
      const now = new Date();
      
      if (period === 'today') {
        dateFilter = 'today';
        
        // Calculate start/end for expense API (needs explicit dates)
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        startDate = todayStart.toISOString();
        endDate = now.toISOString();
      } else if (period === 'yesterday') {
        // CRITICAL FIX: Use date_filter='yesterday' for backend to handle timezone correctly
        dateFilter = 'yesterday';
        
        // Calculate start/end for expense API (needs explicit dates)
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        startDate = yesterday.toISOString();
        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);
        endDate = yesterdayEnd.toISOString();
      } else if (period === 'this_week') {
        // CRITICAL FIX: Let backend handle this_week calculation for consistency
        // Backend doesn't have 'this_week' filter, so calculate dates properly
        const currentDate = new Date(now);
        const dayOfWeek = currentDate.getDay(); // 0 (Sunday) to 6 (Saturday)
        const weekStart = new Date(currentDate);
        weekStart.setDate(currentDate.getDate() - dayOfWeek); // Go to Sunday
        weekStart.setHours(0, 0, 0, 0);
        startDate = weekStart.toISOString();
        endDate = now.toISOString();
      } else if (period === 'this_month') {
        // CRITICAL FIX: Use date_filter='this_month' for backend to handle timezone correctly
        dateFilter = 'this_month';
        
        // Calculate start/end for expense API (needs explicit dates)
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        endDate = now.toISOString();
      } else if (period === 'last_month') {
        // CRITICAL FIX: Use date_filter='last_month' for backend to handle timezone correctly
        dateFilter = 'last_month';
        
        // Calculate start/end for expense API (needs explicit dates)
        const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        startDate = firstDayLastMonth.toISOString();
        // Last day of last month = day 0 of current month
        const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        endDate = lastDayLastMonth.toISOString();
      } else if (period === 'custom' && customRange.start && customRange.end) {
        // Custom date range: send as YYYY-MM-DD format, backend will handle as full day in Jakarta timezone
        startDate = customRange.start; // Already in YYYY-MM-DD format from date input
        endDate = customRange.end;     // Already in YYYY-MM-DD format from date input
      }

      // OPTIMIZED: Single API call for all P&L data
      const analyticsResponse = await axios.get(`${API_URL}/api/analytics/profit-loss`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          date_filter: dateFilter || 'custom',
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          branch_id: selectedBranch !== 'all' ? selectedBranch : undefined
        }
      });

      const backendAnalytics = analyticsResponse.data.analytics || {};

      // OPTIMIZED: Backend calculates everything - just use it directly!
      setAnalytics({
        revenue: backendAnalytics.revenue || { total: 0, ps: 0, fnb: 0, growth: 0 },
        transactions: backendAnalytics.transactions || { total: 0, ps: 0, fnb: 0, avgValue: 0 },
        costs: backendAnalytics.costs || { fnbCogs: 0, electricity: 0, operational: 0, operationalBreakdown: [], total: 0 },
        profit: backendAnalytics.profit || { gross: 0, margin: 0 },
        topProducts: backendAnalytics.topProducts || [],
        allProducts: backendAnalytics.allProducts || [],
        psUtilization: backendAnalytics.psUtilization || [],
        peakHours: backendAnalytics.peakHours || []
      });

    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Gagal memuat data analytics');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    try {
      // Format period label dengan tanggal/bulan yang spesifik
      let periodLabel = '';
      const now = new Date();
      
      if (period === 'today') {
        periodLabel = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      } else if (period === 'yesterday') {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        periodLabel = yesterday.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      } else if (period === 'this_week') {
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        periodLabel = `${startOfWeek.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })} - ${endOfWeek.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      } else if (period === 'this_month') {
        periodLabel = now.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
      } else if (period === 'last_month') {
        const lastMonth = new Date(now);
        lastMonth.setMonth(now.getMonth() - 1);
        periodLabel = lastMonth.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
      } else if (period === 'custom' && customRange.start && customRange.end) {
        const start = new Date(customRange.start);
        const end = new Date(customRange.end);
        periodLabel = `${start.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      } else {
        periodLabel = 'Custom Range';
      }
      
      const branchLabel = selectedBranch === 'all' ? 'Semua Cabang' : 
                         branches.find(b => b.id === selectedBranch)?.name || 'Unknown';
      
      // Format numbers without currency symbol for CSV
      const formatNumber = (num) => {
        return new Intl.NumberFormat('id-ID', { 
          minimumFractionDigits: 0,
          maximumFractionDigits: 0 
        }).format(num);
      };
      
      let csvContent = '\uFEFF'; // UTF-8 BOM for Excel compatibility
      
      // Get user info from localStorage
      let csvUserName = 'Unknown';
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          csvUserName = user.username || user.name || 'Unknown';
        }
      } catch (e) {
        console.error('Error parsing user from localStorage:', e);
      }
      
      // Format date in GMT+7 (WIB)
      const csvExportDate = new Date();
      const csvWibTime = new Date(csvExportDate.getTime() + (7 * 60 * 60 * 1000));
      const csvFormattedDate = csvWibTime.toLocaleString('id-ID', { 
        day: 'numeric',
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC'
      }) + ' WIB';
      
      // ===== HEADER SECTION =====
      csvContent += "LAPORAN ANALYTICS & PROFIT/LOSS\n";
      csvContent += "=".repeat(80) + "\n";
      csvContent += `Periode,${periodLabel}\n`;
      csvContent += `Cabang,${branchLabel}\n`;
      csvContent += `Tanggal Export,${csvFormattedDate}\n`;
      csvContent += `Dibuat oleh,${csvUserName}\n`;
      csvContent += "=".repeat(80) + "\n\n";
      
      // ===== EXECUTIVE SUMMARY =====
      csvContent += "RINGKASAN EKSEKUTIF\n";
      csvContent += "-".repeat(80) + "\n";
      csvContent += "Metrik,Nilai (IDR),Persentase\n";
      csvContent += `Total Pendapatan,${formatNumber(analytics.revenue.total)},-\n`;
      csvContent += `Total Biaya,${formatNumber(analytics.costs.total)},-\n`;
      csvContent += `Laba Bersih,${formatNumber(analytics.profit.gross)},${analytics.profit.margin.toFixed(2)}%\n`;
      csvContent += `Margin Laba,${analytics.profit.margin.toFixed(2)}%,-\n`;
      csvContent += `Total Transaksi,${analytics.transactions.total},-\n`;
      csvContent += `Rata-rata Nilai Transaksi,${formatNumber(analytics.transactions.avgValue)},-\n`;
      csvContent += "\n\n";
      
      // ===== REVENUE BREAKDOWN =====
      csvContent += "RINCIAN PENDAPATAN\n";
      csvContent += "-".repeat(80) + "\n";
      csvContent += "Kategori,Jumlah (IDR),Persentase dari Total,Jumlah Transaksi\n";
      const psPercent = analytics.revenue.total > 0 ? ((analytics.revenue.ps / analytics.revenue.total) * 100).toFixed(1) : 0;
      const fnbPercent = analytics.revenue.total > 0 ? ((analytics.revenue.fnb / analytics.revenue.total) * 100).toFixed(1) : 0;
      csvContent += `PS Rental,${formatNumber(analytics.revenue.ps)},${psPercent}%,${analytics.transactions.ps}\n`;
      csvContent += `F&B Sales,${formatNumber(analytics.revenue.fnb)},${fnbPercent}%,${analytics.transactions.fnb}\n`;
      csvContent += `TOTAL PENDAPATAN,${formatNumber(analytics.revenue.total)},100.0%,${analytics.transactions.total}\n`;
      csvContent += "\n\n";
      
      // ===== COST BREAKDOWN =====
      csvContent += "RINCIAN BIAYA\n";
      csvContent += "-".repeat(80) + "\n";
      csvContent += "Kategori Biaya,Jumlah (IDR),Persentase dari Total Biaya\n";
      const cogsPercent = analytics.costs.total > 0 ? ((analytics.costs.fnbCogs / analytics.costs.total) * 100).toFixed(1) : 0;
      const elecPercent = analytics.costs.total > 0 ? ((analytics.costs.electricity / analytics.costs.total) * 100).toFixed(1) : 0;
      const opPercent = analytics.costs.total > 0 ? ((analytics.costs.operational / analytics.costs.total) * 100).toFixed(1) : 0;
      csvContent += `HPP F&B (COGS),${formatNumber(analytics.costs.fnbCogs)},${cogsPercent}%\n`;
      csvContent += `Listrik,${formatNumber(analytics.costs.electricity)},${elecPercent}%\n`;
      csvContent += `Biaya Operasional,${formatNumber(analytics.costs.operational)},${opPercent}%\n`;
      csvContent += `TOTAL BIAYA,${formatNumber(analytics.costs.total)},100.0%\n`;
      csvContent += "\n\n";
      
      // ===== OPERATIONAL EXPENSES BREAKDOWN =====
      if (analytics.costs.operationalBreakdown && analytics.costs.operationalBreakdown.length > 0) {
        csvContent += "DETAIL BIAYA OPERASIONAL\n";
        csvContent += "-".repeat(80) + "\n";
        csvContent += "Kategori,Jumlah (IDR),Persentase dari Operasional\n";
        analytics.costs.operationalBreakdown.forEach(item => {
          const itemPercent = analytics.costs.operational > 0 ? ((item.total / analytics.costs.operational) * 100).toFixed(1) : 0;
          csvContent += `${item.label},${formatNumber(item.total)},${itemPercent}%\n`;
        });
        csvContent += `TOTAL OPERASIONAL,${formatNumber(analytics.costs.operational)},100.0%\n`;
        csvContent += "\n\n";
      }
      
      // ===== PROFIT & LOSS STATEMENT =====
      csvContent += "LAPORAN LABA RUGI (PROFIT & LOSS STATEMENT)\n";
      csvContent += "-".repeat(80) + "\n";
      csvContent += "Komponen,Jumlah (IDR)\n";
      csvContent += "\nPENDAPATAN,\n";
      csvContent += `  PS Rental,${formatNumber(analytics.revenue.ps)}\n`;
      csvContent += `  F&B Sales,${formatNumber(analytics.revenue.fnb)}\n`;
      csvContent += `TOTAL PENDAPATAN,${formatNumber(analytics.revenue.total)}\n`;
      csvContent += "\nBIAYA,\n";
      csvContent += `  HPP F&B,${formatNumber(analytics.costs.fnbCogs)}\n`;
      csvContent += `  Listrik,${formatNumber(analytics.costs.electricity)}\n`;
      csvContent += `  Operasional,${formatNumber(analytics.costs.operational)}\n`;
      csvContent += `TOTAL BIAYA,(${formatNumber(analytics.costs.total)})\n`;
      csvContent += `\nLABA BERSIH,${formatNumber(analytics.profit.gross)}\n`;
      csvContent += `MARGIN LABA,${analytics.profit.margin.toFixed(2)}%\n`;
      csvContent += "\n\n";
      
      // ===== TOP PRODUCTS =====
      if (analytics.topProducts.length > 0) {
        csvContent += "TOP 5 PRODUK F&B TERLARIS\n";
        csvContent += "-".repeat(80) + "\n";
        csvContent += "Ranking,Nama Produk,Jumlah Terjual,Pendapatan (IDR),Kontribusi\n";
        const totalProductRevenue = analytics.topProducts.reduce((sum, p) => sum + p.revenue, 0);
        analytics.topProducts.forEach((p, idx) => {
          const contribution = totalProductRevenue > 0 ? ((p.revenue / totalProductRevenue) * 100).toFixed(1) : 0;
          csvContent += `${idx + 1},${p.name},${p.qty},${formatNumber(p.revenue)},${contribution}%\n`;
        });
        csvContent += `,,TOTAL,${formatNumber(totalProductRevenue)},100.0%\n`;
        csvContent += "\n\n";
      }
      
      // ===== PS UTILIZATION =====
      if (analytics.psUtilization.length > 0) {
        csvContent += "PERFORMA UNIT PS\n";
        csvContent += "-".repeat(80) + "\n";
        csvContent += "Ranking,Unit PS,Jumlah Rental,Pendapatan (IDR),Kontribusi\n";
        const totalPsRevenue = analytics.psUtilization.reduce((sum, u) => sum + u.revenue, 0);
        analytics.psUtilization.forEach((u, idx) => {
          const contribution = totalPsRevenue > 0 ? ((u.revenue / totalPsRevenue) * 100).toFixed(1) : 0;
          csvContent += `${idx + 1},${u.unit},${u.count},${formatNumber(u.revenue)},${contribution}%\n`;
        });
        csvContent += `,,TOTAL,${formatNumber(totalPsRevenue)},100.0%\n`;
        csvContent += "\n\n";
      }
      
      // ===== PEAK HOURS =====
      if (analytics.peakHours.length > 0) {
        csvContent += "JAM RAMAI (PEAK HOURS)\n";
        csvContent += "-".repeat(80) + "\n";
        csvContent += "Ranking,Jam,Jumlah Transaksi,Persentase\n";
        const totalPeakTrans = analytics.peakHours.reduce((sum, h) => sum + h.count, 0);
        analytics.peakHours.forEach((hour, idx) => {
          const percent = totalPeakTrans > 0 ? ((hour.count / totalPeakTrans) * 100).toFixed(1) : 0;
          csvContent += `${idx + 1},${hour.hour.toString().padStart(2, '0')}:00,${hour.count},${percent}%\n`;
        });
        csvContent += "\n\n";
      }
      
      // ===== FOOTER =====
      csvContent += "=".repeat(80) + "\n";
      csvContent += "Laporan ini dibuat secara otomatis oleh sistem POS\n";
      csvContent += `Generated at: ${new Date().toISOString()}\n`;
      csvContent += "=".repeat(80) + "\n";
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('href', url);
      link.setAttribute('download', `Laporan_Analytics_ProfitLoss_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Report berhasil di-export!', {
        description: 'File CSV telah didownload dengan format profesional',
        duration: 3000
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Gagal export report', {
        description: error.message,
        duration: 4000
      });
    }
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF();
      
      // Format period label dengan tanggal/bulan yang spesifik
      let periodLabel = '';
      const now = new Date();
      
      if (period === 'today') {
        periodLabel = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      } else if (period === 'yesterday') {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        periodLabel = yesterday.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      } else if (period === 'this_week') {
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        periodLabel = `${startOfWeek.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })} - ${endOfWeek.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      } else if (period === 'this_month') {
        periodLabel = now.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
      } else if (period === 'last_month') {
        const lastMonth = new Date(now);
        lastMonth.setMonth(now.getMonth() - 1);
        periodLabel = lastMonth.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
      } else if (period === 'custom' && customRange.start && customRange.end) {
        const start = new Date(customRange.start);
        const end = new Date(customRange.end);
        periodLabel = `${start.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      } else {
        periodLabel = 'Custom Range';
      }
      
      const branchLabel = selectedBranch === 'all' ? 'Semua Cabang' : 
                         branches.find(b => b.id === selectedBranch)?.name || 'Unknown';
      
      // Format number for display
      const formatNumber = (num) => {
        return new Intl.NumberFormat('id-ID', { 
          minimumFractionDigits: 0,
          maximumFractionDigits: 0 
        }).format(num);
      };
      
      let yPosition = 20;
      
      // ===== HEADER =====
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('LAPORAN ANALYTICS & PROFIT/LOSS', 105, yPosition, { align: 'center' });
      
      yPosition += 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Periode: ${periodLabel}`, 105, yPosition, { align: 'center' });
      
      yPosition += 5;
      doc.text(`Cabang: ${branchLabel}`, 105, yPosition, { align: 'center' });
      
      yPosition += 5;
      // Get user info from localStorage
      let userName = 'Unknown';
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          userName = user.username || user.name || 'Unknown';
        }
      } catch (e) {
        console.error('Error parsing user from localStorage:', e);
      }
      
      // Format date in GMT+7 (WIB)
      const exportDate = new Date();
      const wibExportTime = new Date(exportDate.getTime() + (7 * 60 * 60 * 1000));
      const formattedExportDate = wibExportTime.toLocaleString('id-ID', { 
        day: 'numeric',
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC'
      }) + ' WIB';
      
      doc.text(`Tanggal Export: ${formattedExportDate}`, 105, yPosition, { align: 'center' });
      
      yPosition += 5;
      doc.text(`Dibuat oleh: ${userName}`, 105, yPosition, { align: 'center' });
      
      yPosition += 10;
      doc.setDrawColor(0, 0, 0);
      doc.line(14, yPosition, 196, yPosition);
      yPosition += 8;
      
      // ===== EXECUTIVE SUMMARY =====
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('RINGKASAN EKSEKUTIF', 14, yPosition);
      yPosition += 7;
      
      doc.autoTable({
        startY: yPosition,
        head: [['Metrik', 'Nilai (IDR)', 'Persentase']],
        body: [
          ['Total Pendapatan', `Rp ${formatNumber(analytics.revenue.total)}`, '-'],
          ['Total Biaya', `Rp ${formatNumber(analytics.costs.total)}`, '-'],
          ['Laba Bersih', `Rp ${formatNumber(analytics.profit.gross)}`, `${analytics.profit.margin.toFixed(2)}%`],
          ['Margin Laba', `${analytics.profit.margin.toFixed(2)}%`, '-'],
          ['Total Transaksi', analytics.transactions.total.toString(), '-'],
          ['Rata-rata Nilai Transaksi', `Rp ${formatNumber(analytics.transactions.avgValue)}`, '-']
        ],
        theme: 'grid',
        headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 60, halign: 'right' },
          2: { cellWidth: 40, halign: 'center' }
        }
      });
      
      yPosition = doc.lastAutoTable.finalY + 10;
      
      // ===== REVENUE BREAKDOWN =====
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('RINCIAN PENDAPATAN', 14, yPosition);
      yPosition += 7;
      
      const psPercent = analytics.revenue.total > 0 ? ((analytics.revenue.ps / analytics.revenue.total) * 100).toFixed(1) : 0;
      const fnbPercent = analytics.revenue.total > 0 ? ((analytics.revenue.fnb / analytics.revenue.total) * 100).toFixed(1) : 0;
      
      doc.autoTable({
        startY: yPosition,
        head: [['Kategori', 'Jumlah (IDR)', 'Persentase', 'Transaksi']],
        body: [
          ['PS Rental', `Rp ${formatNumber(analytics.revenue.ps)}`, `${psPercent}%`, analytics.transactions.ps.toString()],
          ['F&B Sales', `Rp ${formatNumber(analytics.revenue.fnb)}`, `${fnbPercent}%`, analytics.transactions.fnb.toString()],
          ['TOTAL', `Rp ${formatNumber(analytics.revenue.total)}`, '100.0%', analytics.transactions.total.toString()]
        ],
        theme: 'grid',
        headStyles: { fillColor: [92, 184, 92], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 50, halign: 'right' },
          2: { cellWidth: 35, halign: 'center' },
          3: { cellWidth: 35, halign: 'center' }
        },
        bodyStyles: (data) => {
          if (data.row.index === 2) {
            return { fontStyle: 'bold', fillColor: [240, 240, 240] };
          }
        }
      });
      
      yPosition = doc.lastAutoTable.finalY + 10;
      
      // ===== COST BREAKDOWN =====
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('RINCIAN BIAYA', 14, yPosition);
      yPosition += 7;
      
      const cogsPercent = analytics.costs.total > 0 ? ((analytics.costs.fnbCogs / analytics.costs.total) * 100).toFixed(1) : 0;
      const elecPercent = analytics.costs.total > 0 ? ((analytics.costs.electricity / analytics.costs.total) * 100).toFixed(1) : 0;
      const opPercent = analytics.costs.total > 0 ? ((analytics.costs.operational / analytics.costs.total) * 100).toFixed(1) : 0;
      
      doc.autoTable({
        startY: yPosition,
        head: [['Kategori Biaya', 'Jumlah (IDR)', 'Persentase']],
        body: [
          ['HPP F&B (COGS)', `Rp ${formatNumber(analytics.costs.fnbCogs)}`, `${cogsPercent}%`],
          ['Listrik', `Rp ${formatNumber(analytics.costs.electricity)}`, `${elecPercent}%`],
          ['Biaya Operasional', `Rp ${formatNumber(analytics.costs.operational)}`, `${opPercent}%`],
          ['TOTAL BIAYA', `Rp ${formatNumber(analytics.costs.total)}`, '100.0%']
        ],
        theme: 'grid',
        headStyles: { fillColor: [240, 173, 78], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 60, halign: 'right' },
          2: { cellWidth: 40, halign: 'center' }
        },
        bodyStyles: (data) => {
          if (data.row.index === 3) {
            return { fontStyle: 'bold', fillColor: [240, 240, 240] };
          }
        }
      });
      
      yPosition = doc.lastAutoTable.finalY + 10;
      
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      // ===== OPERATIONAL EXPENSES BREAKDOWN =====
      if (analytics.costs.operationalBreakdown && analytics.costs.operationalBreakdown.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('DETAIL BIAYA OPERASIONAL', 14, yPosition);
        yPosition += 7;
        
        const opExpenseData = analytics.costs.operationalBreakdown.map(item => {
          const itemPercent = analytics.costs.operational > 0 ? ((item.total / analytics.costs.operational) * 100).toFixed(1) : 0;
          return [item.label, `Rp ${formatNumber(item.total)}`, `${itemPercent}%`];
        });
        opExpenseData.push(['TOTAL OPERASIONAL', `Rp ${formatNumber(analytics.costs.operational)}`, '100.0%']);
        
        doc.autoTable({
          startY: yPosition,
          head: [['Kategori', 'Jumlah (IDR)', 'Persentase']],
          body: opExpenseData,
          theme: 'grid',
          headStyles: { fillColor: [217, 83, 79], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 70 },
            1: { cellWidth: 60, halign: 'right' },
            2: { cellWidth: 40, halign: 'center' }
          },
          bodyStyles: (data) => {
            if (data.row.index === opExpenseData.length - 1) {
              return { fontStyle: 'bold', fillColor: [240, 240, 240] };
            }
          }
        });
        
        yPosition = doc.lastAutoTable.finalY + 10;
      }
      
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      // ===== PROFIT & LOSS STATEMENT =====
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('LAPORAN LABA RUGI (P&L)', 14, yPosition);
      yPosition += 7;
      
      doc.autoTable({
        startY: yPosition,
        head: [['Komponen', 'Jumlah (IDR)']],
        body: [
          [{ content: 'PENDAPATAN', styles: { fontStyle: 'bold', fillColor: [245, 245, 245] } }, ''],
          ['  PS Rental', `Rp ${formatNumber(analytics.revenue.ps)}`],
          ['  F&B Sales', `Rp ${formatNumber(analytics.revenue.fnb)}`],
          [{ content: 'TOTAL PENDAPATAN', styles: { fontStyle: 'bold' } }, { content: `Rp ${formatNumber(analytics.revenue.total)}`, styles: { fontStyle: 'bold' } }],
          ['', ''],
          [{ content: 'BIAYA', styles: { fontStyle: 'bold', fillColor: [245, 245, 245] } }, ''],
          ['  HPP F&B', `Rp ${formatNumber(analytics.costs.fnbCogs)}`],
          ['  Listrik', `Rp ${formatNumber(analytics.costs.electricity)}`],
          ['  Operasional', `Rp ${formatNumber(analytics.costs.operational)}`],
          [{ content: 'TOTAL BIAYA', styles: { fontStyle: 'bold' } }, { content: `(Rp ${formatNumber(analytics.costs.total)})`, styles: { fontStyle: 'bold' } }],
          ['', ''],
          [{ content: 'LABA BERSIH', styles: { fontStyle: 'bold', fillColor: analytics.profit.gross >= 0 ? [223, 240, 216] : [242, 222, 222] } }, 
           { content: `Rp ${formatNumber(analytics.profit.gross)}`, styles: { fontStyle: 'bold', fillColor: analytics.profit.gross >= 0 ? [223, 240, 216] : [242, 222, 222] } }],
          [{ content: 'MARGIN LABA', styles: { fontStyle: 'bold' } }, { content: `${analytics.profit.margin.toFixed(2)}%`, styles: { fontStyle: 'bold' } }]
        ],
        theme: 'grid',
        headStyles: { fillColor: [91, 192, 222], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 90 },
          1: { cellWidth: 80, halign: 'right' }
        }
      });
      
      yPosition = doc.lastAutoTable.finalY + 10;
      
      // Check if we need a new page
      if (yPosition > 240) {
        doc.addPage();
        yPosition = 20;
      }
      
      // ===== TOP PRODUCTS =====
      if (analytics.topProducts.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('TOP 5 PRODUK F&B TERLARIS', 14, yPosition);
        yPosition += 7;
        
        const totalProductRevenue = analytics.topProducts.reduce((sum, p) => sum + p.revenue, 0);
        const productData = analytics.topProducts.map((p, idx) => {
          const contribution = totalProductRevenue > 0 ? ((p.revenue / totalProductRevenue) * 100).toFixed(1) : 0;
          return [(idx + 1).toString(), p.name, p.qty.toString(), `Rp ${formatNumber(p.revenue)}`, `${contribution}%`];
        });
        productData.push(['', 'TOTAL', '', `Rp ${formatNumber(totalProductRevenue)}`, '100.0%']);
        
        doc.autoTable({
          startY: yPosition,
          head: [['#', 'Nama Produk', 'Terjual', 'Pendapatan', 'Kontribusi']],
          body: productData,
          theme: 'grid',
          headStyles: { fillColor: [138, 109, 59], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 10, halign: 'center' },
            1: { cellWidth: 60 },
            2: { cellWidth: 25, halign: 'center' },
            3: { cellWidth: 45, halign: 'right' },
            4: { cellWidth: 30, halign: 'center' }
          },
          bodyStyles: (data) => {
            if (data.row.index === productData.length - 1) {
              return { fontStyle: 'bold', fillColor: [240, 240, 240] };
            }
          }
        });
        
        yPosition = doc.lastAutoTable.finalY + 10;
      }
      
      // Check if we need a new page
      if (yPosition > 240) {
        doc.addPage();
        yPosition = 20;
      }
      
      // ===== PS UTILIZATION =====
      if (analytics.psUtilization.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('PERFORMA UNIT PS', 14, yPosition);
        yPosition += 7;
        
        const totalPsRevenue = analytics.psUtilization.reduce((sum, u) => sum + u.revenue, 0);
        const psData = analytics.psUtilization.map((u, idx) => {
          const contribution = totalPsRevenue > 0 ? ((u.revenue / totalPsRevenue) * 100).toFixed(1) : 0;
          return [(idx + 1).toString(), u.unit, u.count.toString(), `Rp ${formatNumber(u.revenue)}`, `${contribution}%`];
        });
        psData.push(['', 'TOTAL', '', `Rp ${formatNumber(totalPsRevenue)}`, '100.0%']);
        
        doc.autoTable({
          startY: yPosition,
          head: [['#', 'Unit PS', 'Rental', 'Pendapatan', 'Kontribusi']],
          body: psData,
          theme: 'grid',
          headStyles: { fillColor: [102, 51, 153], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 10, halign: 'center' },
            1: { cellWidth: 60 },
            2: { cellWidth: 25, halign: 'center' },
            3: { cellWidth: 45, halign: 'right' },
            4: { cellWidth: 30, halign: 'center' }
          },
          bodyStyles: (data) => {
            if (data.row.index === psData.length - 1) {
              return { fontStyle: 'bold', fillColor: [240, 240, 240] };
            }
          }
        });
        
        yPosition = doc.lastAutoTable.finalY + 10;
      }
      
      // Check if we need a new page
      if (yPosition > 260) {
        doc.addPage();
        yPosition = 20;
      }
      
      // ===== PEAK HOURS =====
      if (analytics.peakHours.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('JAM RAMAI (PEAK HOURS)', 14, yPosition);
        yPosition += 7;
        
        const totalPeakTrans = analytics.peakHours.reduce((sum, h) => sum + h.count, 0);
        const peakData = analytics.peakHours.map((hour, idx) => {
          const percent = totalPeakTrans > 0 ? ((hour.count / totalPeakTrans) * 100).toFixed(1) : 0;
          return [(idx + 1).toString(), `${hour.hour.toString().padStart(2, '0')}:00`, hour.count.toString(), `${percent}%`];
        });
        
        doc.autoTable({
          startY: yPosition,
          head: [['#', 'Jam', 'Transaksi', 'Persentase']],
          body: peakData,
          theme: 'grid',
          headStyles: { fillColor: [51, 122, 183], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 15, halign: 'center' },
            1: { cellWidth: 40, halign: 'center' },
            2: { cellWidth: 40, halign: 'center' },
            3: { cellWidth: 40, halign: 'center' }
          }
        });
        
        yPosition = doc.lastAutoTable.finalY + 10;
      }
      
      // ===== FOOTER =====
      const pageCount = doc.internal.getNumberOfPages();
      
      // Get user info from localStorage (same as header)
      let footerUserName = 'Unknown';
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          footerUserName = user.username || user.name || 'Unknown';
        }
      } catch (e) {
        console.error('Error parsing user from localStorage:', e);
      }
      
      // Format date in GMT+7 (WIB) for footer
      const footerNow = new Date();
      const wibTime = new Date(footerNow.getTime() + (7 * 60 * 60 * 1000)); // Add 7 hours for WIB
      const formattedDate = wibTime.toISOString().replace('T', ' ').substring(0, 19) + ' WIB';
      
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(128);
        doc.text('Laporan ini dibuat secara otomatis oleh sistem POS', 105, 285, { align: 'center' });
        doc.text(`Dibuat oleh: ${footerUserName} | ${formattedDate}`, 105, 290, { align: 'center' });
        doc.text(`Halaman ${i} dari ${pageCount}`, 195, 290, { align: 'right' });
      }
      
      // Save PDF
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      doc.save(`Laporan_Analytics_ProfitLoss_${timestamp}.pdf`);
      
      toast.success('PDF Report berhasil di-export!', {
        description: 'File PDF telah didownload dengan format profesional',
        duration: 3000
      });
      
    } catch (error) {
      console.error('Export PDF error:', error);
      toast.error('Gagal export PDF report', {
        description: error.message,
        duration: 4000
      });
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header & Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
               Analytics & Profit/Loss
              {loading && (
                <div className="flex items-center gap-2 text-sm font-normal text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  Memuat data...
                </div>
              )}
            </h2>
            <p className="text-sm text-gray-600 mt-1">Dashboard analisa bisnis & laporan keuangan</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowExpensesModal(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition flex items-center gap-2"
            >
              <Receipt className="h-4 w-4" />
              Kelola Pengeluaran
            </button>
            <button
              onClick={exportReport}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            <button
              onClick={exportPDF}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
            >
              <FileText className="h-4 w-4" />
              Export PDF
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Period Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Periode
            </label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="today">Hari Ini</option>
              <option value="yesterday">Kemarin</option>
              <option value="this_week">Minggu Ini</option>
              <option value="this_month">Bulan Ini</option>
              <option value="last_month">Bulan Kemarin</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Branch Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Package className="h-4 w-4 inline mr-1" />
              Cabang
            </label>
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Semua Cabang</option>
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </select>
          </div>

          {/* Custom Date Range (if period is custom) */}
          {period === 'custom' && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Tanggal</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={customRange.start}
                  onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="flex items-center px-2">s/d</span>
                <input
                  type="date"
                  value={customRange.end}
                  onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 transition-opacity duration-300 ${loading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
        {/* Total Revenue */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="h-8 w-8" />
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Total</span>
          </div>
          <h3 className="text-sm font-medium opacity-90">Total Pendapatan</h3>
          <p className="text-2xl font-bold mt-1">{formatCurrency(analytics.revenue.total)}</p>
        </div>

        {/* Total Transactions */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <ShoppingCart className="h-8 w-8" />
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Transaksi</span>
          </div>
          <h3 className="text-sm font-medium opacity-90">Total Transaksi</h3>
          <p className="text-2xl font-bold mt-1">{analytics.transactions.total}</p>
          <p className="text-xs mt-1 opacity-75">Avg: {formatCurrency(analytics.transactions.avgValue)}</p>
        </div>

        {/* Total Costs */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <TrendingDown className="h-8 w-8" />
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Biaya</span>
          </div>
          <h3 className="text-sm font-medium opacity-90">Total Biaya</h3>
          <p className="text-2xl font-bold mt-1">{formatCurrency(analytics.costs.total)}</p>
        </div>

        {/* Gross Profit */}
        <div className={`bg-gradient-to-br ${analytics.profit.gross >= 0 ? 'from-green-500 to-green-600' : 'from-red-500 to-red-600'} rounded-lg p-6 text-white shadow-lg`}>
          <div className="flex items-center justify-between mb-2">
            {analytics.profit.gross >= 0 ? <TrendingUp className="h-8 w-8" /> : <TrendingDown className="h-8 w-8" />}
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">{analytics.profit.margin.toFixed(1)}%</span>
          </div>
          <h3 className="text-sm font-medium opacity-90">Laba Bersih</h3>
          <p className="text-2xl font-bold mt-1">{formatCurrency(analytics.profit.gross)}</p>
        </div>
      </div>

      {/* Revenue Breakdown & Profit/Loss Report */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Revenue Breakdown */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Revenue Breakdown
          </h3>
          
          <div className="space-y-4">
            {/* PS Revenue */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Gamepad2 className="h-4 w-4 text-blue-600" />
                  PS Rental
                </span>
                <span className="text-sm font-bold text-gray-900">{formatCurrency(analytics.revenue.ps)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${analytics.revenue.total > 0 ? (analytics.revenue.ps / analytics.revenue.total) * 100 : 0}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {analytics.revenue.total > 0 ? ((analytics.revenue.ps / analytics.revenue.total) * 100).toFixed(1) : 0}% dari total
              </p>
            </div>

            {/* F&B Revenue with Details */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-orange-600" />
                  F&B Sales
                </span>
                <span className="text-sm font-bold text-gray-900">{formatCurrency(analytics.revenue.fnb)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-600 h-2 rounded-full transition-all"
                  style={{ width: `${analytics.revenue.total > 0 ? (analytics.revenue.fnb / analytics.revenue.total) * 100 : 0}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {analytics.revenue.total > 0 ? ((analytics.revenue.fnb / analytics.revenue.total) * 100).toFixed(1) : 0}% dari total
              </p>

              {/* Detailed F&B Product List */}
              {analytics.allProducts && analytics.allProducts.length > 0 && (
                <div className="mt-4 border-t pt-3">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Detail Produk F&B:</p>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {analytics.allProducts.map((product, idx) => (
                      <div key={idx} className="bg-orange-50 rounded-md p-2 text-xs">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-gray-800">{product.name}</span>
                          <span className="text-orange-600 font-bold">{formatCurrency(product.revenue)}</span>
                        </div>
                        <div className="text-gray-600 space-y-0.5">
                          <div className="flex justify-between">
                            <span>{product.qty} pcs × {formatCurrency(product.revenue / product.qty)}</span>
                            <span className="text-gray-500">Revenue</span>
                          </div>
                          <div className="flex justify-between border-t border-orange-200 pt-0.5">
                            <span>HPP: {formatCurrency(product.hpp)}/pcs × {product.qty}</span>
                            <span className="text-red-600 font-medium">{formatCurrency(product.cogs)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-orange-300 flex justify-between text-xs font-bold">
                    <span className="text-gray-700">Total F&B COGS:</span>
                    <span className="text-red-600">{formatCurrency(analytics.costs.fnbCogs)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Profit/Loss Report */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-green-600" />
            Laporan Laba Rugi
          </h3>

          <div className="space-y-3">
            {/* Revenue Section */}
            <div className="border-b pb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Pendapatan</p>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">PS Rental</span>
                  <span className="font-medium">{formatCurrency(analytics.revenue.ps)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">F&B Sales</span>
                  <span className="font-medium">{formatCurrency(analytics.revenue.fnb)}</span>
                </div>
                <div className="flex justify-between text-sm font-bold pt-2 border-t">
                  <span>Total Pendapatan</span>
                  <span className="text-blue-600">{formatCurrency(analytics.revenue.total)}</span>
                </div>
              </div>
            </div>

            {/* Costs Section */}
            <div className="border-b pb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Biaya</p>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">HPP F&B (COGS)</span>
                  <span className="font-medium">{formatCurrency(analytics.costs.fnbCogs)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    Listrik
                  </span>
                  <span className="font-medium">{formatCurrency(analytics.costs.electricity)}</span>
                </div>
                
                {/* Operational Breakdown */}
                {analytics.costs.operationalBreakdown && analytics.costs.operationalBreakdown.length > 0 ? (
                  <div className="pl-4 border-l-2 border-orange-200 space-y-1 my-2">
                    <p className="text-xs font-semibold text-orange-600 mb-1">Operasional:</p>
                    {analytics.costs.operationalBreakdown.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-xs">
                        <span className="text-gray-500">{item.label}</span>
                        <span className="font-medium text-gray-700">{formatCurrency(item.total)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Operasional</span>
                    <span className="font-medium">{formatCurrency(analytics.costs.operational)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm font-bold pt-2 border-t">
                  <span>Total Biaya</span>
                  <span className="text-orange-600">{formatCurrency(analytics.costs.total)}</span>
                </div>
              </div>
            </div>

            {/* Profit */}
            <div>
              <div className="flex justify-between items-center">
                <span className="text-base font-bold text-gray-900">Laba Bersih</span>
                <div className="text-right">
                  <span className={`text-xl font-black ${analytics.profit.gross >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(analytics.profit.gross)}
                  </span>
                  <p className="text-xs text-gray-500">Margin: {analytics.profit.margin.toFixed(2)}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Products & PS Utilization */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top F&B Products */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4"> Top 5 Produk F&B</h3>
          
          {analytics.topProducts.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">Belum ada data</p>
          ) : (
            <div className="space-y-3">
              {analytics.topProducts.map((product, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-gray-300">#{idx + 1}</span>
                    <div>
                      <p className="font-semibold text-gray-900">{product.name}</p>
                      <p className="text-xs text-gray-500">{product.qty} terjual</p>
                    </div>
                  </div>
                  <span className="font-bold text-blue-600">{formatCurrency(product.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PS Unit Utilization */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4"> PS Unit Performance</h3>
          
          {analytics.psUtilization.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">Belum ada data</p>
          ) : (
            <div className="space-y-3">
              {analytics.psUtilization.map((unit, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-gray-300">#{idx + 1}</span>
                    <div>
                      <p className="font-semibold text-gray-900">{unit.unit}</p>
                      <p className="text-xs text-gray-500">{unit.count} rental</p>
                    </div>
                  </div>
                  <span className="font-bold text-green-600">{formatCurrency(unit.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Peak Hours */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4"> Jam Ramai (Peak Hours)</h3>
        
        {analytics.peakHours.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4">Belum ada data</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {analytics.peakHours.map((hour, idx) => (
              <div key={idx} className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-4 text-white text-center">
                <p className="text-3xl font-black">{hour.hour.toString().padStart(2, '0')}:00</p>
                <p className="text-sm opacity-90 mt-1">{hour.count} transaksi</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Operational Expenses Modal */}
      <OperationalExpensesModal
        isOpen={showExpensesModal}
        onClose={() => {
          setShowExpensesModal(false);
          fetchAnalytics(); // Refresh analytics after closing modal
        }}
        branches={branches}
        selectedBranch={selectedBranch}
        period={period}
        customRange={customRange}
      />
    </div>
  );
}
